import { createContext, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  OrganisationTypeConstants,
  ProjectActivityReviewsConstants,
  ProjectPermissionConstants,
} from "../../../constants";
import { ActivityReviewPageParams } from "../../../models";
import { getPublicGroupDetails } from "../../../service/publicquery/PublicQueryService";
import {
  getActivityDetails,
  GetActivityDetailsResponse,
  GetActivityReviewAggregateResponse,
  getActivityReviewDetails,
  getCurrentUserProjectPermissions,
  searchActivityReviews,
  SearchActivityReviewsResponse,
  searchDiscussionThreads,
} from "../../../service/query";
import { getActivityReviewAggregate } from "../../../service/query/QueryService.full";
import { Status } from "../../../service/Shared";
import { useAuth } from "../../../useAuth";
import { hasPermissionForProject } from "../../../utils";
import { getDashboardRoute } from "../../../utils/routes";
import { dataGridMapFilterCriteria } from "../../../widget";

interface ActivityReviewDashboardProviderProps {
  children?: JSX.Element | JSX.Element[];
}

/**
 *  Given that we need to be able to account for multiple reviews in the
 *  workflow, we base this context around the activityUuid
 */
interface ActivityReviewDashboardContextType {
  activityReviews: SearchActivityReviewsResponse | undefined;
  activityReviewActivityUuid: string | undefined;
  activityReviewAggregate: GetActivityReviewAggregateResponse | undefined;
  activityDetails: GetActivityDetailsResponse | undefined;
  currentActivityReviewUuid: string | undefined; // For auditor we know if we need the assessment / review so we can populate this for navigation purposes
  currentActivityReviewType: string | undefined;
  queryParams: string | undefined;
  hasManageProjectActivityReviewPermission: boolean;
  hasWriteProjectActivityReviewPermission: boolean;
  canReviewActivity: boolean;
  hasUnreadDiscussions: boolean;
  isAssignedAuditor: boolean;
  showDetailsPanel: boolean;
  refreshActivityReviews: boolean;
  currentOrganisationUuid: string | undefined;
  isLoading: boolean;
  setRefreshActivityReviews: Dispatch<SetStateAction<boolean>>;
  setShowDetailsPanel: Dispatch<SetStateAction<boolean>>;
  onCloseDetailsPanel: () => void;
  setHasUnreadDiscussions: (val: boolean) => void;
  refreshResolvedDiscussionsKPI: () => Promise<void>;
  refreshUnreadDiscussionsTabIndicator: () => Promise<void>;
}

export const ActivityReviewDashboardContext = createContext<ActivityReviewDashboardContextType>(
  {} as ActivityReviewDashboardContextType
);

export const ActivityReviewDashboardProvider = ({ children }: ActivityReviewDashboardProviderProps): JSX.Element => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const userUuid = useMemo(() => user?.userUuid ?? "", [user]);

  const [currentOrganisationUuid, setCurrentOrganisationUuid] = useState<string | undefined>(undefined);
  const [activityReviews, setActivityReviews] = useState<SearchActivityReviewsResponse>();
  const { activityReviewActivityUuid } = useParams<ActivityReviewPageParams>();
  const [currentActivityReviewUuid, setCurrentActivityReviewUuid] = useState<string>();
  const [activityDetails, setActivityDetails] = useState<GetActivityDetailsResponse>();
  const [activityReviewAggregate, setActivityReviewAggregate] = useState<GetActivityReviewAggregateResponse>();

  const [hasManageProjectActivityReviewPermission, setHasManageProjectActivityReviewPermission] = useState(true);
  const [hasWriteProjectActivityReviewPermission, setHasWriteProjectActivityReviewPermission] = useState(true);
  const [isAssignedAuditor, setIsAssignedAuditor] = useState(true);
  const [canReviewActivity, setCanReviewActivity] = useState(false);
  const [hasUnreadDiscussions, setHasUnreadDiscussions] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [refreshActivityReviews, setRefreshActivityReviews] = useState(false);

  const [showDetailsPanel, setShowDetailsPanel] = useState(false);

  const urlActivityReviewType = searchParams.get("type") ?? undefined;
  // For an assessor we don't require `type` in the URL: it's derived from the review assigned to the
  // current user once the reviews have been fetched (see fetchActivityReviewDetails).
  const [derivedActivityReviewType, setDerivedActivityReviewType] = useState<string>();
  const currentActivityReviewType = urlActivityReviewType ?? derivedActivityReviewType;

  const URLprojectUuid = searchParams.get("projectUuid");
  const URLorigin = searchParams.get("origin");
  // eslint-disable-next-line no-nested-ternary
  let queryParams = URLprojectUuid ? `projectUuid=${URLprojectUuid}` : URLorigin ? `origin=${URLorigin}` : "";

  if (queryParams === "") queryParams = currentActivityReviewType ? `type=${currentActivityReviewType}` : "";
  else queryParams = currentActivityReviewType ? `${queryParams}&type=${currentActivityReviewType}` : queryParams;

  const onCloseDetailsPanel = (): void => {
    setShowDetailsPanel(false);
  };

  const refreshResolvedDiscussionsKPI = async (): Promise<void> => {
    // for discussions KPI
    await getActivityReviewAggregate({
      activityReviewUuid: null,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      activityUuid: activityReviewActivityUuid!,
      assignedToUserUuid: null,
    }).then((res) => {
      if (res.status === Status.Success && res.data) {
        setActivityReviewAggregate(res.data);
      }
    });
  };

  const refreshUnreadDiscussionsTabIndicator = async (): Promise<void> => {
    if (!activityReviewActivityUuid) return;

    let afterCursor: string | null = null;
    let hasUnread = false;
    let paging = true;

    while (paging) {
      // eslint-disable-next-line no-await-in-loop
      const res = await searchDiscussionThreads({
        paging: { afterCursor, beforeCursor: null, limit: 50 },
        objectUuid: activityReviewActivityUuid,
        objectType: "Activity",
        isDraft: true,
        filter: {
          results: { messages: { some: { createdByUser: { uuid: { operator: "neq", value: userUuid } } } } },
        } as never,
      });

      if (res.status !== Status.Success || !res.data) {
        paging = false;
      } else {
        hasUnread = res.data.results.some((thread) => {
          const latest = [...thread.messages].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          )[thread.messages.length - 1];
          return latest && latest.createdByUser.uuid !== userUuid;
        });

        if (hasUnread || !res.data.paging.hasNextPage) {
          paging = false;
        } else {
          afterCursor = res.data.paging.endCursor;
        }
      }
    }

    setHasUnreadDiscussions(hasUnread);
  };

  const fetchActivityReviewDetails = useCallback(
    async (uuid: string) => {
      const filterCriteria = dataGridMapFilterCriteria([]);
      filterCriteria.activity = {
        uuid: { operator: "eq", value: uuid },
      };

      filterCriteria.status = {
        operator: "neq",
        value: ProjectActivityReviewsConstants.STATUS_CANCELLED,
      };

      const response = await searchActivityReviews({
        filter: { results: filterCriteria },
        paging: {
          afterCursor: null,
          beforeCursor: null,
          limit: 10,
        },
      });
      if (response.status === Status.Success && response.data) {
        setActivityReviews(response.data);

        // When the URL doesn't carry a `type`, derive it from the (non-cancelled) review assigned to
        // the current user so an assessor can land on the dashboard without passing the query param.
        let effectiveActivityReviewType = urlActivityReviewType;
        if (effectiveActivityReviewType == null) {
          const assignedReview = response.data.results.find(
            (review) =>
              review.status !== ProjectActivityReviewsConstants.STATUS_CANCELLED &&
              review.assignedToUser?.uuid === userUuid
          );
          effectiveActivityReviewType = assignedReview?.type;
          setDerivedActivityReviewType(effectiveActivityReviewType);
        }

        let isUnderReview = false;
        let currentActivityReviewStatus: string | undefined;
        let isCurrentUserAnAssignedAuditor = false;
        if (effectiveActivityReviewType != null) {
          const currentActivityReview = response.data.results.find(
            (review) =>
              review.type === effectiveActivityReviewType &&
              review.status !== ProjectActivityReviewsConstants.STATUS_CANCELLED
          );
          if (currentActivityReview != null) {
            setCurrentActivityReviewUuid(currentActivityReview?.activityReviewUuid);
            currentActivityReviewStatus = currentActivityReview?.status;

            const activityReviewDetailsResponse = await getActivityReviewDetails({
              activityReviewUuid: currentActivityReview?.activityReviewUuid,
            });

            isCurrentUserAnAssignedAuditor =
              activityReviewDetailsResponse?.data?.assignedToUserUuids.some(
                (assignedUserUuid: string) => assignedUserUuid === user?.userUuid
              ) ?? false;
            setIsAssignedAuditor(isCurrentUserAnAssignedAuditor);
            isUnderReview = activityReviewDetailsResponse?.data?.isUnderReview ?? false;
          }
        } else {
          setIsAssignedAuditor(false);
        }

        let projectUuid = response.data.results[0].project?.uuid;
        if (!projectUuid?.length && response.data.results[0].group?.uuid) {
          const groupDetailsRes = await getPublicGroupDetails({
            projectGroupUuid: response.data.results[0].group?.uuid,
          });

          projectUuid = groupDetailsRes.data.projects[0].uuid;
        }

        if (!projectUuid?.length) {
          return;
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        await getCurrentUserProjectPermissions({ projectUuids: [projectUuid!], groupUuids: null }).then(
          (permissions) => {
            if (permissions.status === Status.Success && permissions.data) {
              setHasManageProjectActivityReviewPermission(
                hasPermissionForProject(
                  permissions.data,
                  projectUuid,
                  ProjectPermissionConstants.MANAGE_PROJECT_ACTIVITY_REVIEW
                )
              );

              const hasWritePermission = hasPermissionForProject(
                permissions.data,
                projectUuid,
                ProjectPermissionConstants.WRITE_PROJECT_ACTIVITY_REVIEW
              );

              setHasWriteProjectActivityReviewPermission(hasWritePermission);
              // Logic for BR-0725
              setCanReviewActivity(
                hasWritePermission &&
                  (currentActivityReviewStatus === ProjectActivityReviewsConstants.STATUS_IN_PROGRESS ||
                    currentActivityReviewStatus === ProjectActivityReviewsConstants.STATUS_FEEDBACK_RECEIVED) &&
                  isCurrentUserAnAssignedAuditor &&
                  isUnderReview
              );
            } else {
              setHasManageProjectActivityReviewPermission(false);
              setHasWriteProjectActivityReviewPermission(false);
              setCanReviewActivity(false);
            }
          }
        );

        const activityUuid = response.data.results[0].activity.uuid;

        await getActivityDetails({ activityUuid }).then(async (activityDetailsResponse) => {
          setActivityDetails(activityDetailsResponse.data);
          setCurrentOrganisationUuid(activityDetailsResponse.data?.project.verifier?.organisationUuid || undefined);
          await refreshUnreadDiscussionsTabIndicator();
          await refreshResolvedDiscussionsKPI();
        });
      }
    },
    [activityReviewActivityUuid, refreshActivityReviews]
  );

  useEffect(() => {
    if (userUuid && activityReviewActivityUuid && activityReviewActivityUuid.length > 10) {
      fetchActivityReviewDetails(activityReviewActivityUuid).finally(() => setIsLoading(false));
    }
  }, [activityReviewActivityUuid, refreshActivityReviews, userUuid]);

  useEffect(() => {
    if (!isAssignedAuditor && !hasManageProjectActivityReviewPermission)
      navigate(getDashboardRoute(OrganisationTypeConstants.VERIFIER));
  }, [isAssignedAuditor, hasManageProjectActivityReviewPermission]);

  const memoisedValue = useMemo(
    () => ({
      activityReviewActivityUuid,
      activityReviews,
      activityDetails,
      activityReviewAggregate,
      currentActivityReviewUuid,
      currentActivityReviewType,
      queryParams,
      hasManageProjectActivityReviewPermission,
      hasWriteProjectActivityReviewPermission,
      isAssignedAuditor,
      canReviewActivity,
      hasUnreadDiscussions,
      showDetailsPanel,
      refreshActivityReviews,
      currentOrganisationUuid,
      isLoading,

      setRefreshActivityReviews,
      onCloseDetailsPanel,
      setShowDetailsPanel,
      setHasUnreadDiscussions,
      refreshResolvedDiscussionsKPI,
      refreshUnreadDiscussionsTabIndicator,
    }),
    [
      activityReviewActivityUuid,
      activityReviews,
      activityDetails,
      showDetailsPanel,
      activityReviewAggregate,
      hasUnreadDiscussions,
      refreshActivityReviews,
      currentActivityReviewUuid,
      currentActivityReviewType,
      queryParams,

      hasManageProjectActivityReviewPermission,
      hasWriteProjectActivityReviewPermission,
      canReviewActivity,
    ]
  );

  return (
    <ActivityReviewDashboardContext.Provider value={memoisedValue}>{children}</ActivityReviewDashboardContext.Provider>
  );
};
